import React, { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Play, Sparkles } from "lucide-react";
import { api, mediaUrl, fmtUSD, fmtINR } from "@/lib/api";
import { feedSpecs } from "@/lib/useModels";
import { useListNav } from "@/components/NavArrows";
import FeedDetail from "@/components/FeedDetail";
import HoverVideo from "@/components/HoverVideo";

const shuffle = (arr) => { const a = [...arr]; for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; };

const COLS = { full: "columns-2 sm:columns-3 lg:columns-4 2xl:columns-5", compact: "columns-2 xl:columns-3" };

const FeedCard = ({ item, onOpen, i }) => (
  <div className="mb-3 break-inside-avoid">
  <button type="button" onClick={() => onOpen(item)} data-testid={`feed-card-${item.id}`}
    style={{ animationDelay: `${Math.min(i, 12) * 40}ms` }}
    className={`feed-card group relative flex flex-col w-full overflow-hidden rounded-2xl bg-[#111113] border border-zinc-800/70 text-left ax-fade-up`}>
    <div className="relative bg-black">
      {item.kind === "video"
        ? <HoverVideo src={mediaUrl(item.url)} autoInView className="w-full h-auto block" testid={`feed-video-${item.id}`} />
        : <img src={mediaUrl(item.url)} alt={item.prompt} loading="lazy" className="w-full h-auto block" />}
      {item.kind === "video" && <span className="absolute top-2.5 left-2.5 ax-badge !bg-black/80 !text-white !border-white/20 flex items-center gap-1 pointer-events-none"><Play className="w-2.5 h-2.5 fill-current" />Video</span>}
    </div>
    <div className="p-3 flex flex-col gap-1.5" data-testid={`feed-card-details-${item.id}`}>
      <div className="flex items-center justify-between gap-2 text-[10px] font-mono text-zinc-400">
        <span className="truncate text-[#7AF25F]" data-testid={`feed-card-model-${item.id}`}>{item.model_name}</span>
        <span className="truncate">{item.creator}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[10px] font-mono text-zinc-500" data-testid={`feed-card-specs-${item.id}`}>
        {feedSpecs(item).map((x) => <span key={x}>{x}</span>)}
        {item.cost_usd != null && <span className="ml-auto text-zinc-300" data-testid={`feed-card-cost-${item.id}`}>{fmtUSD(item.cost_usd)} · {fmtINR(item.cost_inr)}</span>}
      </div>
    </div>
  </button>
  </div>
);

export default function PublicGrid({ compact = false, emptyHint }) {
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const feedNav = useListNav(items, open, setOpen);
  const [failed, setFailed] = useState(false);
  const sentinel = useRef(null);

  const load = useCallback(async (before) => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/feed", { params: { limit: 30, before: before || undefined } });
      setItems((p) => (before ? [...p, ...shuffle(data.items)] : shuffle(data.items)));
      setNext(data.next);
      setFailed(false);
    } catch { setFailed(true); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(null); }, [load]);
  useEffect(() => {
    const el = sentinel.current;
    if (!el || !next) return undefined;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && !loading && load(next), { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [next, loading, load]);

  if (!loading && !items.length && failed) {
    return (
      <div className="flex flex-col items-center justify-center text-center gap-3 py-24" data-testid="feed-error">
        <div className="font-heading text-lg text-zinc-200">Couldn't load the showcase</div>
        <button onClick={() => load(null)} className="ax-chip" data-testid="feed-retry">Try again</button>
      </div>
    );
  }
  if (!loading && !items.length) {
    return (
      <div className="flex flex-col items-center justify-center text-center gap-3 py-24" data-testid="feed-empty">
        <div className="h-14 w-14 rounded-2xl border border-zinc-800 bg-[#121214] flex items-center justify-center"><Sparkles className="w-6 h-6 text-[#7AF25F]" /></div>
        <div className="font-heading text-lg text-zinc-200">Nothing published yet</div>
        <p className="text-sm text-zinc-500 max-w-sm">{emptyHint || "Published images and videos will appear here."}</p>
      </div>
    );
  }
  return (
    <>
      <div className={`${COLS[compact ? "compact" : "full"]} gap-3`} data-testid="feed-grid">
        {items.map((it, i) => <FeedCard key={it.id} item={it} i={i} onOpen={setOpen} />)}
      </div>
      <div ref={sentinel} className="h-10 flex items-center justify-center">{loading && <Loader2 className="w-5 h-5 animate-spin text-zinc-500" data-testid="feed-loading" />}</div>
      <FeedDetail item={open} onClose={() => setOpen(null)} nav={feedNav} />
    </>
  );
}
